import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import GUI from "lil-gui";

/**
 * Base
 */
// Debug
const gui = new GUI();

// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scene
const scene = new THREE.Scene();

/**
 * Galaxy
 */

const parameters = {};
parameters.count = 100000;
parameters.size = 0.001;
parameters.sideWidth = 5;

let geometry = null;
let material = null;
let points = null;

const generateTriangle = () => {
	if (points !== null) {
		geometry.dispose();
		material.dispose();
		scene.remove(points);
	}

	/**
	 * Geometry
	 */
	const positions = new Float32Array(parameters.count * 3);
	const colors = new Float32Array(parameters.count * 3);

	/**
	 * Sommets du triangle - positions 1 à 9 (indices 0 à 8)
	 */

	positions[0] = 0;
	positions[1] = parameters.sideWidth / Math.sqrt(3);
	positions[2] = 0;

	positions[3] = -parameters.sideWidth / 2;
	positions[4] = -parameters.sideWidth / (2 * Math.sqrt(3));
	positions[5] = 0;

	positions[6] = parameters.sideWidth / 2;
	positions[7] = -parameters.sideWidth / (2 * Math.sqrt(3));
	positions[8] = 0;

	/**
	 * Couleurs des sommets du triangle
	 */

	colors[0] = 1.0;
	colors[1] = 0.0;
	colors[2] = 0.0;

	colors[3] = 0.0;
	colors[4] = 1.0;
	colors[5] = 0.0;

	colors[6] = 0.0;
	colors[7] = 0.0;
	colors[8] = 1.0;

	// Coordonnées des trois sommets du triangle
	const vertex1 = { x: 0, y: parameters.sideWidth / Math.sqrt(3), z: 0 };
	const vertex2 = {
		x: -parameters.sideWidth / 2,
		y: -parameters.sideWidth / (2 * Math.sqrt(3)),
		z: 0,
	};
	const vertex3 = {
		x: parameters.sideWidth / 2,
		y: -parameters.sideWidth / (2 * Math.sqrt(3)),
		z: 0,
	};

	const vertices = [vertex1, vertex2, vertex3];

	let u = Math.random();
	let v = Math.random();

	// S'assurer que le point est dans le triangle
	if (u + v > 1) {
		u = 1 - u;
		v = 1 - v;
	}

	const w = 1 - u - v;

	// Calculer la position du point en utilisant les coordonnées barycentriques
	let pointX = u * vertex1.x + v * vertex2.x + w * vertex3.x;
	let pointY = u * vertex1.y + v * vertex2.y + w * vertex3.y;

	for (let i = 3; i < parameters.count; i++) {
		const i3 = i * 3;

		const randomVertex = vertices[Math.floor(Math.random() * 3)];

		// Calculer le milieu entre le point aléatoire et le sommet choisi

		const newPoint = {
			x: (pointX + randomVertex.x) / 2,
			y: (pointY + randomVertex.y) / 2,
			z: 0,
		};

        pointX = newPoint.x
        pointY = newPoint.y

		positions[i3 + 0] = newPoint.x;
		positions[i3 + 1] = newPoint.y;
		positions[i3 + 2] = newPoint.z;

		// Couleur aléatoire pour chaque point
        // Calculer la distance au centre (0, 0)
        const distanceToCenter = Math.sqrt(newPoint.x * newPoint.x + newPoint.y * newPoint.y);

        // Exemple : utiliser la distance pour définir la couleur (plus proche du centre = rouge, plus loin = bleu)
        // Normaliser la distance par rapport au rayon maximal (distance d'un sommet au centre)
        const maxDistance = parameters.sideWidth / Math.sqrt(3);
        const t = Math.min(distanceToCenter / maxDistance, 1);

        colors[i3 + 0] = 1 - t; 
        colors[i3 + 1] = t/2;
        colors[i3 + 2] = t;
	}

	geometry = new THREE.BufferGeometry();
	geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

	geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));

	/**
	 * Material
	 */

	material = new THREE.PointsMaterial({
		size: parameters.size,
		sizeAttenuation: true,
		depthWrite: false,
		vertexColors: true,
	});

	/**
	 * Points
	 */
	points = new THREE.Points(geometry, material);
	scene.add(points);
};

generateTriangle();

/**
 * GUI tweaks
 */

gui.add(parameters, "count")
	.min(100)
	.max(1000000)
	.step(1000)
	.onFinishChange(generateTriangle);
gui.add(parameters, "size")
	.min(0.00001)
	.max(0.001)
	.step(0.00001)
	.onChange(generateTriangle);
gui.add(parameters, "sideWidth")
	.min(1)
	.max(10)
	.step(1)
	.onChange(generateTriangle);

/**
 * Sizes
 */
const sizes = {
	width: window.innerWidth,
	height: window.innerHeight,
};

window.addEventListener("resize", () => {
	// Update sizes
	sizes.width = window.innerWidth;
	sizes.height = window.innerHeight;

	// Update camera
	camera.aspect = sizes.width / sizes.height;
	camera.updateProjectionMatrix();

	// Update renderer
	renderer.setSize(sizes.width, sizes.height);
	renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(
	75,
	sizes.width / sizes.height,
	0.001,
	100
);
camera.position.x = 3;
camera.position.y = 3;
camera.position.z = 3;
scene.add(camera);

// Controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
	canvas: canvas,
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

/**
 * Animate
 */
const clock = new THREE.Clock();

const tick = () => {
	const elapsedTime = clock.getElapsedTime();

	// Update controls
	controls.update();

	// Render
	renderer.render(scene, camera);

    points.rotation.y = elapsedTime / 20

	// Call tick again on the next frame
	window.requestAnimationFrame(tick);
};

tick();
